/**
 * RaceHubReturn — the post-race lobby's "way out" for players (Event
 * Control Round A E.4).
 *
 * After a race the E.2 loop lands everyone on gm_race_post_lobby where the
 * NEXT race lobby auto-opens — racers who want another round click Start /
 * stay joined. This floating button serves everyone else: it leaves the
 * race flow for the social hub.
 *
 * Navigation: the hub lives on the /lobby MODE PATH. We can NOT use the
 * requestMapSwitch path here — navigateToMap keeps the current pathname,
 * and /gmrace's coerceModeUrl force-sets `map=gm_race_lobby` on every boot
 * of that path (coerceModeUrl.ts:124), which would bounce the player right
 * back into the race lobby. Known hub map ids map to their mode paths;
 * anything else lands on the bare `/?map=…` form (no coercion, the
 * BiomeNexusReturn precedent).
 *
 * Visibility: on the post-race lobby map only, and never during
 * staging/live (racers mid-flow shouldn't see an exit button).
 * Destination: the map's `metadata.hubMapId` (authored in
 * data/levels/gm_race_post_lobby.json), fallback 'dj-live-lobby'.
 *
 * @see docs/design/gm/EVENT_CONTROL_SPEC.md §3.6
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LevelRegistry } from '../../systems/map/LevelRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { isMainGameDomain } from '../../../config/environment/environment';
import { bootSearch } from '../../../config/boot/bootUrl';
import { navigateInGame } from '../../../config/boot/inGameNavigation';
import { buildMapTravelUrl } from '../../services/travel/mapTravelUrl';

const POST_LOBBY_MAP = 'gm_race_post_lobby';
const DEFAULT_HUB_MAP = 'dj-live-lobby';
/** Hub maps that live behind a mode path — MUST navigate by path so the
 *  mode's own coercion sets the params (see header). */
const MAP_TO_MODE_PATH: Record<string, string> = {
  'dj-live-lobby': '/lobby',
};

const POLL_MS = 500;

function getRoom(): any {
  return (globalThis as any).nm?.getRoom?.() ?? null;
}

function readHubMapId(): string {
  const level = LevelRegistry.getInstance().getLevel(POST_LOBBY_MAP) as
    { metadata?: { hubMapId?: string } } | null;
  return level?.metadata?.hubMapId ?? DEFAULT_HUB_MAP;
}

/** Navigate out of the race flow to the hub. Exported for the results HUD's
 *  footer button so both exits share one implementation. */
export function leaveToHub(): void {
  const hubMapId = readHubMapId();
  // Suppress the "Connection Lost" overlay + spurious reconnect on the
  // intentional socket close (the navigateToMap courtesy).
  const nm = ServiceRegistry.getInstance().get('NetworkManager', { optional: true }) as
    { markNavigating?: () => void } | null;
  nm?.markNavigating?.();
  navigateInGame(raceHubUrl(hubMapId, isMainGameDomain(), bootSearch()));
}

/**
 * Where the way out goes. helix2000.com has no mode paths (D44): the hero
 * travels to the hub like a portal trip, keeping the character.
 */
export function raceHubUrl(hubMapId: string, mainGame: boolean, currentSearch: string): string {
  if (mainGame) return buildMapTravelUrl(currentSearch, { mapId: hubMapId });
  return MAP_TO_MODE_PATH[hubMapId]
    ?? `/?map=${encodeURIComponent(hubMapId)}&preset=live-lobby&skipSplash=1&skipLogin=1`;
}

function isVisible(): boolean {
  const state = getRoom()?.state;
  if (!state || state.mapId !== POST_LOBBY_MAP) return false;
  const phase: string = state.gmEvent?.phase ?? '';
  return phase !== 'staging' && phase !== 'live';
}

export const RaceHubReturn: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const tick = () => setVisible(isVisible());
    tick();
    const interval = setInterval(tick, POLL_MS);
    return () => clearInterval(interval);
  }, []);

  if (!visible) return null;

  return createPortal(
    <button
      data-testid="race-hub-return"
      onClick={leaveToHub}
      style={{
        position: 'fixed',
        bottom: 96,
        right: 16,
        zIndex: 8400,
        padding: '8px 14px',
        borderRadius: 8,
        border: '1px solid rgba(236, 240, 241, 0.3)',
        background: 'rgba(30, 34, 45, 0.9)',
        color: '#ecf0f1',
        fontSize: 12,
        cursor: 'pointer',
      }}
      title="Leave the race flow and return to the social hub"
    >
      🏠 Leave to Lobby
    </button>,
    document.body,
  );
};
